import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Box, Stack } from '@mui/material';

import Button from '../../components/Button/Button';
import { useLocalStoragePersistState } from '../../hooks/useLocalStoragePersistState';
import { Step1 } from './components/FormSteps/Step1';
import { Step2 } from './components/FormSteps/Step2';
import { Step3 } from './components/FormSteps/Step3';
import Stepper from './components/Stepper';
import { FormValue, StepOneFormValue, StepTwoFormValue } from './components/schema';

export type Method = 'insert' | 'update';

type OfferFormProps = {
  formConfig: { initialValues: FormValue; onSubmit: (values: FormValue) => void };
  method: Method;
  offerTypeIndex?: number;
  isShopifyProduct?: boolean;
};

const ProductForm: React.FC<OfferFormProps> = ({ formConfig, isShopifyProduct = false }) => {
  const navigate = useNavigate();
  const scrollDivRef = React.useRef<HTMLDivElement | null>(null);

  const [activeStep, setActiveStep] = useLocalStoragePersistState<number>('product-form-step', 0);

  const [formData, setFormData] = useLocalStoragePersistState<FormValue>(
    'product-form-data',
    formConfig.initialValues,
  );

  React.useEffect(() => {
    setFormData(formConfig.initialValues);
  }, [formConfig.initialValues, setFormData]);

  React.useEffect(() => {
    if (scrollDivRef.current) {
      scrollDivRef.current.scrollTo({ top: 0, behavior: 'smooth' }); // Scroll to the top of the div
    }
  }, [activeStep]);

  const handleNextStepOne = (values: StepOneFormValue) => {
    setFormData((prev) => ({ ...prev, ...values }));
    setActiveStep((prev) => prev + 1);
  };

  const handleNextStepTwo = (values: StepOneFormValue) => {
    setFormData((prev) => ({ ...prev, ...values }));
    setActiveStep((prev) => prev + 1);
  };

  const handleNextStepThree = (values: StepTwoFormValue & StepOneFormValue) => {
    const _values = { ...formData, ...values };
    return formConfig.onSubmit(_values);
  };

  const handleBackStep = () => {
    if (activeStep === 0) {
      navigate('/products');
    } else {
      setActiveStep((prev) => prev - 1);
    }
  };

  const handlePersistFormDataStep = (values: Partial<FormValue>) => {
    setFormData((prev) => ({
      ...prev,
      ...values,
    }));
  };

  const steps = [
    <Step1
      data={formData}
      handleNext={handleNextStepOne}
      handlePersistFormDataStep={handlePersistFormDataStep}
      isShopifyProduct={isShopifyProduct}
    />,
    <Step2
      data={formData}
      handleNext={handleNextStepTwo}
      handlePersistFormDataStep={handlePersistFormDataStep}
    />,
    <Step3
      data={formData}
      handleNext={handleNextStepThree}
      handlePersistFormDataStep={handlePersistFormDataStep}
    />,
  ];

  return (
    <Box
      sx={{
        backgroundColor: 'white',
        py: 4,
        display: 'flex',
        width: '100%',
        justifyContent: 'center',
        flexDirection: 'column',
        flexGrow: 1,
        position: 'relative',
        overflow: 'auto',
        height: '100%',
      }}
      ref={scrollDivRef}
    >
      <Box
        component='div'
        maxWidth='sm'
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          width: '100%',
          height: '100%',
          m: 'auto',
        }}
      >
        {formData.type !== 'private' && <Stepper activeStep={activeStep} />}

        <Button
          startIcon={<ArrowBackIcon />}
          aria-label='back'
          color='primary'
          sx={{ position: 'absolute', left: '5%', top: '2%' }}
          onClick={handleBackStep}
        >
          Back
        </Button>

        <Stack spacing={6} width={'100%'}>
          <Box pb={6}>{steps[activeStep]}</Box>
        </Stack>
      </Box>
    </Box>
  );
};

export default ProductForm;
